import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { prCampaignsAPI } from '../api/prCampaigns';
import { ArrowLeft, ExternalLink, RefreshCw, Loader2, Pencil, Plus, EyeOff, RotateCcw, ChevronDown, ChevronRight, ThumbsUp, Minus, ThumbsDown, Globe, TrendingUp, Newspaper, MapPin, PieChart, X } from 'lucide-react';
import RegionHeatMap from '../components/RegionHeatMap';

// Helper: get favicon URL for a source/domain
const getFaviconUrl = (source, url) => {
  let domain = null;
  if (url) {
    try {
      domain = new URL(url).hostname.replace(/^www\./, '');
      // Skip Google News redirect domains
      if (domain === 'news.google.com') domain = null;
    } catch {}
  }
  if (!domain && source) {
    const clean = source.trim().toLowerCase().replace(/\s+on\s+(msn|yahoo|aol|flipboard)$/i, '').replace(/^www\./, '');
    domain = clean.includes('.') ? clean : `${clean.replace(/\s+/g, '')}.com`;
  }
  if (!domain) return null;
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
};

// Helper: reach tier badge
const getReachTier = (reach) => {
  if (!reach) return null;
  if (reach >= 50_000_000) return { label: 'Tier 1', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', title: '50M+ monthly visitors' };
  if (reach >= 10_000_000) return { label: 'Tier 2', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', title: '10M+ monthly visitors' };
  if (reach >= 1_000_000) return { label: 'Tier 3', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', title: '1M+ monthly visitors' };
  if (reach >= 100_000) return { label: 'Tier 4', color: 'bg-green-500/20 text-green-400 border-green-500/30', title: '100K+ monthly visitors' };
  return { label: 'Niche', color: 'bg-white/10 text-white border-white', title: 'Under 100K monthly visitors' };
};

const sentimentConfig = {
  positive: { icon: ThumbsUp, color: 'text-green-400 bg-green-500/20 border-green-500/30 hover:bg-green-500/30', label: 'Positive' },
  neutral: { icon: Minus, color: 'text-white bg-white/10 border-white hover:bg-white/15', label: 'Neutral' },
  negative: { icon: ThumbsDown, color: 'text-red-400 bg-red-500/20 border-red-500/30 hover:bg-red-500/30', label: 'Negative' },
};

const PrCampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showAddArticle, setShowAddArticle] = useState(false);
  const [addingArticle, setAddingArticle] = useState(false);
  const [articleForm, setArticleForm] = useState({ title: '', url: '', source: '', publishedAt: '' });
  const [actioningId, setActioningId] = useState(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [sovData, setSovData] = useState(null);
  const [sovLoading, setSovLoading] = useState(false);
  const [competitorInput, setCompetitorInput] = useState('');

  const fetchCampaign = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await prCampaignsAPI.getOne(id);
      setCampaign(response.data.campaign);
    } catch (err) {
      console.error('Error fetching PR campaign:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaign();
  }, [id]);

  const toDateInput = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toISOString().split('T')[0];
  };

  const startEditing = () => {
    setEditForm({
      gameTitle: campaign.gameTitle,
      notes: campaign.notes || '',
      startDate: toDateInput(campaign.startDate),
      endDate: toDateInput(campaign.endDate),
      includeKeywords: (campaign.includeKeywords || []).join(', '),
      excludeKeywords: (campaign.excludeKeywords || []).join(', '),
      competitors: campaign.competitors || [],
    });
    setCompetitorInput('');
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await prCampaignsAPI.update(id, {
        gameTitle: editForm.gameTitle,
        notes: editForm.notes || null,
        startDate: editForm.startDate || null,
        endDate: editForm.endDate || null,
        includeKeywords: editForm.includeKeywords ? editForm.includeKeywords.split(',').map(k => k.trim()).filter(Boolean) : [],
        excludeKeywords: editForm.excludeKeywords ? editForm.excludeKeywords.split(',').map(k => k.trim()).filter(Boolean) : [],
        competitors: editForm.competitors || [],
      });
      setEditing(false);
      fetchCampaign();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleAddArticle = async (e) => {
    e.preventDefault();
    if (!articleForm.title.trim() || !articleForm.url.trim()) return;
    try {
      setAddingArticle(true);
      await prCampaignsAPI.addArticle(id, {
        title: articleForm.title,
        url: articleForm.url,
        source: articleForm.source || null,
        publishedAt: articleForm.publishedAt || null,
      });
      setArticleForm({ title: '', url: '', source: '', publishedAt: '' });
      setShowAddArticle(false);
      fetchCampaign();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add article');
    } finally {
      setAddingArticle(false);
    }
  };

  const handleDismiss = async (e, articleId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      setActioningId(articleId);
      await prCampaignsAPI.dismissArticle(id, articleId);
      fetchCampaign(true);
    } catch (err) {
      alert('Failed to dismiss article');
    } finally {
      setActioningId(null);
    }
  };

  const handleRestore = async (e, articleId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      setActioningId(articleId);
      await prCampaignsAPI.restoreArticle(id, articleId);
      fetchCampaign(true);
    } catch (err) {
      alert('Failed to restore article');
    } finally {
      setActioningId(null);
    }
  };

  const handleSentiment = async (e, articleId, sentiment) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      setActioningId(articleId);
      await prCampaignsAPI.updateSentiment(id, articleId, sentiment);
      fetchCampaign(true);
    } catch (err) {
      alert('Failed to update sentiment');
    } finally {
      setActioningId(null);
    }
  };

  const handlePoll = async () => {
    try {
      setPolling(true);
      const response = await prCampaignsAPI.poll(id);
      alert(`Found ${response.data.added} new articles (${response.data.total} total)`);
      fetchCampaign();
    } catch (err) {
      alert('Failed to check for new articles');
    } finally {
      setPolling(false);
    }
  };

  const handleFetchSov = async () => {
    try {
      setSovLoading(true);
      const response = await prCampaignsAPI.fetchShareOfVoice(id);
      setSovData(response.data.shareOfVoice);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to fetch Share of Voice');
    } finally {
      setSovLoading(false);
    }
  };

  const addCompetitor = () => {
    const val = competitorInput.trim();
    if (!val) return;
    const current = editForm.competitors || [];
    if (current.some(c => c.toLowerCase() === val.toLowerCase())) return;
    setEditForm({ ...editForm, competitors: [...current, val] });
    setCompetitorInput('');
  };

  const removeCompetitor = (index) => {
    const updated = [...(editForm.competitors || [])];
    updated.splice(index, 1);
    setEditForm({ ...editForm, competitors: updated });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown date';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatRelative = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 size={32} className="animate-spin text-sc-pink" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen p-6" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a14 50%, #0a0a1a 100%)' }}>
        <div className="bg-gray-900 border border-white rounded-xl shadow-lg p-12 text-center">
          <h3 className="text-xl font-bold text-white mb-2">PR Campaign Not Found</h3>
          <button onClick={() => navigate('/dashboard/pr-campaigns')} className="btn-pink mt-4">
            Back to PR Campaigns
          </button>
        </div>
      </div>
    );
  }

  const articles = campaign.PrArticle || [];
  const dismissedArticles = campaign.dismissedArticles || [];
  const totalReach = articles.reduce((sum, a) => sum + (a.reach || 0), 0);
  const uniqueSources = new Set(articles.map((a) => a.source).filter(Boolean)).size;

  // Sentiment stats
  const sentimentCounts = { positive: 0, neutral: 0, negative: 0, untagged: 0 };
  articles.forEach(a => {
    if (a.sentiment && sentimentCounts[a.sentiment] !== undefined) sentimentCounts[a.sentiment]++;
    else sentimentCounts.untagged++;
  });

  // Region stats
  const regionCounts = {};
  articles.forEach(a => {
    const r = a.region || 'Unknown';
    regionCounts[r] = (regionCounts[r] || 0) + 1;
  });

  // Reach tier stats
  const tierCounts = { 'Tier 1': 0, 'Tier 2': 0, 'Tier 3': 0, 'Tier 4': 0, 'Niche': 0 };
  articles.forEach(a => {
    const tier = getReachTier(a.reach);
    if (tier) tierCounts[tier.label]++;
  });

  const formatReach = (num) => {
    if (!num) return null;
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(0)}K`;
    return num.toLocaleString();
  };

  const darkInput = 'w-full px-3 py-2 rounded-lg bg-white/5 border border-white text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-sc-pink focus:border-transparent';

  return (
    <div className="min-h-screen p-6" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a14 50%, #0a0a1a 100%)' }}>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/pr-campaigns')}
          className="flex items-center gap-1 text-sm text-white hover:text-sc-pink mb-3 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to PR Campaigns
        </button>

        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="text-3xl">📰</span>
              <h2 className="text-3xl font-bold text-white">{campaign.gameTitle}</h2>
            </div>
            {campaign.notes && (
              <p className="text-white ml-12">{campaign.notes}</p>
            )}
            <div className="ml-12 mt-1 space-y-1">
              {(campaign.startDate || campaign.endDate) && (
                <p className="text-sm text-white">
                  Date range: {campaign.startDate && campaign.endDate
                    ? `${formatDate(campaign.startDate)} - ${formatDate(campaign.endDate)}`
                    : campaign.startDate
                    ? `From ${formatDate(campaign.startDate)}`
                    : `Until ${formatDate(campaign.endDate)}`}
                </p>
              )}
              {(campaign.includeKeywords?.length > 0 || campaign.excludeKeywords?.length > 0) && (
                <div className="flex flex-wrap gap-1">
                  {campaign.includeKeywords?.map((kw, i) => (
                    <span key={`inc-${i}`} className="text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">+{kw}</span>
                  ))}
                  {campaign.excludeKeywords?.map((kw, i) => (
                    <span key={`exc-${i}`} className="text-xs bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">-{kw}</span>
                  ))}
                </div>
              )}
              <p className="text-sm text-white">
                Last checked: {formatRelative(campaign.lastCheckedAt)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={startEditing}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-white text-white rounded-lg hover:bg-white/5 transition-colors"
            >
              <Pencil size={16} />
              Edit
            </button>
            <button
              onClick={() => setShowAddArticle(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-white text-white rounded-lg hover:bg-white/5 transition-colors"
            >
              <Plus size={16} />
              Add Article
            </button>
            <button
              onClick={handlePoll}
              disabled={polling}
              className="btn-pink flex items-center gap-2"
            >
              {polling ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <RefreshCw size={16} />
              )}
              {polling ? 'Checking...' : 'Check for New Articles'}
            </button>
          </div>
        </div>
      </div>

      {/* Edit Form */}
      {editing && (
        <div className="bg-gray-900 border border-white rounded-xl shadow-lg p-6 mb-6 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-20 blur-2xl" style={{ background: '#ec4899' }} />
          <h3 className="text-lg font-bold text-white mb-4 relative z-10">Edit Campaign</h3>
          <div className="space-y-4 relative z-10">
            <div>
              <label className="block text-sm font-medium text-white mb-1">Game Title *</label>
              <input
                type="text"
                value={editForm.gameTitle}
                onChange={(e) => setEditForm({ ...editForm, gameTitle: e.target.value })}
                className={darkInput}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">Notes</label>
              <textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={2}
                className={darkInput}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Start Date</label>
                <input
                  type="date"
                  value={editForm.startDate}
                  onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                  className={darkInput}
                />
                <p className="text-xs text-white mt-1">Only show articles published after this date</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">End Date</label>
                <input
                  type="date"
                  value={editForm.endDate}
                  onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
                  className={darkInput}
                />
                <p className="text-xs text-white mt-1">Only show articles published before this date</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Include Keywords</label>
                <input
                  type="text"
                  value={editForm.includeKeywords}
                  onChange={(e) => setEditForm({ ...editForm, includeKeywords: e.target.value })}
                  placeholder="e.g. review, preview, interview"
                  className={darkInput}
                />
                <p className="text-xs text-white mt-1">Comma-separated. Articles must contain at least one of these.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Exclude Keywords</label>
                <input
                  type="text"
                  value={editForm.excludeKeywords}
                  onChange={(e) => setEditForm({ ...editForm, excludeKeywords: e.target.value })}
                  placeholder="e.g. walkthrough, guide, mod"
                  className={darkInput}
                />
                <p className="text-xs text-white mt-1">Comma-separated. Articles with these words are filtered out.</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">Competitors (Share of Voice)</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={competitorInput}
                  onChange={(e) => setCompetitorInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCompetitor(); } }}
                  placeholder="e.g. Elden Ring, God of War"
                  className={`flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-sc-pink focus:border-transparent`}
                />
                <button
                  type="button"
                  onClick={addCompetitor}
                  className="px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/15 transition-colors text-sm border border-white"
                >
                  Add
                </button>
              </div>
              {(editForm.competitors || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {editForm.competitors.map((comp, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-sm bg-indigo-500/20 text-indigo-300 px-2.5 py-1 rounded-lg border border-indigo-500/30">
                      {comp}
                      <button type="button" onClick={() => removeCompetitor(i)} className="text-indigo-400 hover:text-indigo-200">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-white mt-1">Add competitor game titles to compare coverage share.</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={saving || !editForm.gameTitle?.trim()}
                className="btn-pink flex items-center gap-2"
              >
                {saving && <Loader2 size={16} className="animate-spin" />}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="px-4 py-2 text-white hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Article Form */}
      {showAddArticle && (
        <div className="bg-gray-900 border border-white rounded-xl shadow-lg p-6 mb-6 relative overflow-hidden">
          <div className="absolute -top-10 -left-10 w-32 h-32 rounded-full opacity-20 blur-2xl" style={{ background: '#8b5cf6' }} />
          <h3 className="text-lg font-bold text-white mb-4 relative z-10">Add Article Manually</h3>
          <form onSubmit={handleAddArticle} className="space-y-4 relative z-10">
            <div>
              <label className="block text-sm font-medium text-white mb-1">Article Title *</label>
              <input
                type="text"
                value={articleForm.title}
                onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                placeholder="e.g. Game Review: An Amazing Experience"
                className={darkInput}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">URL *</label>
              <input
                type="url"
                value={articleForm.url}
                onChange={(e) => setArticleForm({ ...articleForm, url: e.target.value })}
                placeholder="https://..."
                className={darkInput}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Source / Publication</label>
                <input
                  type="text"
                  value={articleForm.source}
                  onChange={(e) => setArticleForm({ ...articleForm, source: e.target.value })}
                  placeholder="e.g. IGN, Kotaku, PCGamer"
                  className={darkInput}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Published Date</label>
                <input
                  type="date"
                  value={articleForm.publishedAt}
                  onChange={(e) => setArticleForm({ ...articleForm, publishedAt: e.target.value })}
                  className={darkInput}
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={addingArticle || !articleForm.title.trim() || !articleForm.url.trim()}
                className="btn-pink flex items-center gap-2"
              >
                {addingArticle && <Loader2 size={16} className="animate-spin" />}
                {addingArticle ? 'Adding...' : 'Add Article'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddArticle(false);
                  setArticleForm({ title: '', url: '', source: '', publishedAt: '' });
                }}
                className="px-4 py-2 text-white hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Summary Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-900 border border-white rounded-xl shadow-lg p-4 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full opacity-20 blur-2xl" style={{ background: '#ec4899' }} />
          <div className="flex items-center gap-2 mb-1 relative z-10">
            <Newspaper size={14} className="text-sc-pink" />
            <p className="text-sm text-white">Articles</p>
          </div>
          <p className="text-2xl font-bold text-white relative z-10">{articles.length}</p>
          <p className="text-xs text-white mt-1 relative z-10">{uniqueSources} sources</p>
        </div>
        <div className="bg-gray-900 border border-white rounded-xl shadow-lg p-4 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full opacity-20 blur-2xl" style={{ background: '#3b82f6' }} />
          <div className="flex items-center gap-2 mb-1 relative z-10">
            <TrendingUp size={14} className="text-blue-400" />
            <p className="text-sm text-white">Combined Reach</p>
          </div>
          <p className="text-2xl font-bold text-white relative z-10">
            {totalReach > 0 ? formatReach(totalReach) : 'Pending'}
          </p>
          <p className="text-xs text-white mt-1 relative z-10">monthly visitors</p>
        </div>
        <div className="bg-gray-900 border border-white rounded-xl shadow-lg p-4 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full opacity-20 blur-2xl" style={{ background: '#22c55e' }} />
          <div className="flex items-center gap-2 mb-1 relative z-10">
            <ThumbsUp size={14} className="text-green-400" />
            <p className="text-sm text-white">Sentiment</p>
          </div>
          <div className="flex items-center gap-2 mt-1 relative z-10">
            {sentimentCounts.positive > 0 && (
              <span className="text-sm font-semibold text-green-400">{sentimentCounts.positive} +</span>
            )}
            {sentimentCounts.neutral > 0 && (
              <span className="text-sm font-semibold text-white">{sentimentCounts.neutral} ~</span>
            )}
            {sentimentCounts.negative > 0 && (
              <span className="text-sm font-semibold text-red-400">{sentimentCounts.negative} -</span>
            )}
            {sentimentCounts.untagged > 0 && (
              <span className="text-xs text-white">{sentimentCounts.untagged} untagged</span>
            )}
          </div>
        </div>
        <div className="bg-gray-900 border border-white rounded-xl shadow-lg p-4 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full opacity-20 blur-2xl" style={{ background: '#a855f7' }} />
          <div className="flex items-center gap-2 mb-1 relative z-10">
            <MapPin size={14} className="text-purple-400" />
            <p className="text-sm text-white">Regions</p>
          </div>
          <div className="flex flex-wrap gap-1 mt-1 relative z-10">
            {Object.entries(regionCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([region, count]) => (
              <span key={region} className="text-xs bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded">
                {region} ({count})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Share of Voice + Geographic Coverage side by side */}
      {((campaign.competitors?.length > 0) || (articles.length > 0 && Object.keys(regionCounts).some(r => r !== 'Unknown'))) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Share of Voice */}
          {(campaign.competitors?.length > 0) && (
            <div className="bg-gray-900 border border-white rounded-xl shadow-lg p-5 relative overflow-hidden">
              <div className="absolute -top-10 -left-10 w-32 h-32 rounded-full opacity-15 blur-2xl" style={{ background: '#6366f1' }} />
              <div className="flex items-center justify-between mb-4 relative z-10">
                <div className="flex items-center gap-2">
                  <PieChart size={16} className="text-indigo-400" />
                  <h3 className="font-bold text-white">Share of Voice</h3>
                  <span className="text-xs text-white">vs {campaign.competitors.length}</span>
                </div>
                <button
                  onClick={handleFetchSov}
                  disabled={sovLoading}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-indigo-500/20 text-indigo-300 rounded-lg hover:bg-indigo-500/30 transition-colors border border-indigo-500/30"
                >
                  {sovLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  {sovLoading ? 'Fetching...' : sovData ? 'Refresh' : 'Calculate'}
                </button>
              </div>

              {sovData ? (() => {
                const total = Object.values(sovData).reduce((sum, c) => sum + c, 0);
                const sorted = Object.entries(sovData).sort((a, b) => b[1] - a[1]);
                const colors = ['bg-sc-pink', 'bg-indigo-400', 'bg-blue-400', 'bg-emerald-400', 'bg-amber-400', 'bg-purple-400', 'bg-rose-400', 'bg-teal-400'];

                return (
                  <div className="space-y-3 relative z-10">
                    {/* Stacked bar */}
                    <div className="flex rounded-full overflow-hidden h-7">
                      {sorted.map(([title, count], i) => {
                        const pct = total > 0 ? (count / total) * 100 : 0;
                        if (pct === 0) return null;
                        return (
                          <div
                            key={title}
                            className={`${colors[i % colors.length]} flex items-center justify-center text-white text-xs font-semibold transition-all`}
                            style={{ width: `${pct}%`, minWidth: pct > 5 ? undefined : '20px' }}
                            title={`${title}: ${count} articles (${Math.round(pct)}%)`}
                          >
                            {pct >= 15 ? `${Math.round(pct)}%` : ''}
                          </div>
                        );
                      })}
                    </div>

                    {/* Legend */}
                    <div className="space-y-1.5">
                      {sorted.map(([title, count], i) => {
                        const pct = total > 0 ? (count / total) * 100 : 0;
                        const isOurs = title === campaign.gameTitle;
                        return (
                          <div key={title} className={`flex items-center gap-2 p-2 rounded-lg ${isOurs ? 'bg-pink-500/15 border border-pink-500/30' : 'bg-white/5'}`}>
                            <div className={`w-3 h-3 rounded-full flex-shrink-0 ${colors[i % colors.length]}`} />
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm font-medium truncate ${isOurs ? 'text-sc-pink' : 'text-white'}`}>
                                {title} {isOurs && <span className="text-xs font-normal text-pink-400">(You)</span>}
                              </p>
                            </div>
                            <p className="text-xs text-white flex-shrink-0">{count} &middot; {Math.round(pct)}%</p>
                          </div>
                        );
                      })}
                    </div>

                    {total === 0 && (
                      <p className="text-sm text-white text-center py-2">No articles found for any titles in the current date range.</p>
                    )}
                  </div>
                );
              })() : (
                <p className="text-sm text-white text-center py-4 relative z-10">
                  Click "Calculate" to fetch article counts and compare coverage against competitors.
                </p>
              )}
            </div>
          )}

          {/* Geographic Coverage Map */}
          {articles.length > 0 && Object.keys(regionCounts).some(r => r !== 'Unknown') && (
            <div className="bg-gray-900 border border-white rounded-xl shadow-lg p-4 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-15 blur-2xl" style={{ background: '#a855f7' }} />
              <div className="flex items-center gap-2 mb-2 relative z-10">
                <MapPin size={14} className="text-purple-400" />
                <h3 className="text-sm font-bold text-white">Geographic Coverage</h3>
                <span className="text-xs text-white">{Object.keys(regionCounts).filter(r => r !== 'Unknown').length} regions</span>
              </div>
              <RegionHeatMap regionCounts={regionCounts} />
            </div>
          )}
        </div>
      )}

      {/* Articles List */}
      {articles.length === 0 ? (
        <div className="bg-gray-900 border border-white rounded-xl shadow-lg p-12 text-center">
          <h3 className="text-xl font-bold text-white mb-2">No Articles Found Yet</h3>
          <p className="text-white mb-4">
            Articles will appear here as they're discovered from Google News and Bing News. Click "Check for New Articles" to search now.
          </p>
        </div>
      ) : (
        <div className="bg-gray-900 border border-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <h3 className="font-bold text-white">Press Articles ({articles.length})</h3>
            <div className="flex items-center gap-3 text-xs text-white">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400"></span> Tier 1: {tierCounts['Tier 1']}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400"></span> Tier 2: {tierCounts['Tier 2']}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400"></span> Tier 3: {tierCounts['Tier 3']}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400"></span> Tier 4: {tierCounts['Tier 4']}</span>
            </div>
          </div>
          <div className="divide-y divide-gray-800/50">
            {articles.map((article) => {
              const favicon = getFaviconUrl(article.source, article.url);
              const tier = getReachTier(article.reach);
              const currentSentiment = article.sentiment;

              return (
                <div
                  key={article.id}
                  className="flex items-start gap-4 px-5 py-4 hover:bg-white/5 transition-colors group"
                >
                  {/* Favicon */}
                  <div className="flex-shrink-0 mt-1">
                    {favicon ? (
                      <img
                        src={favicon}
                        alt=""
                        className="w-6 h-6 rounded"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <Globe size={24} className="text-white/20" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-white group-hover:text-sc-pink transition-colors line-clamp-2 hover:underline"
                    >
                      {article.title}
                    </a>
                    <div className="flex items-center flex-wrap gap-2 mt-1.5">
                      {article.source && (
                        <span className="text-sm font-medium text-indigo-400">{article.source}</span>
                      )}
                      <span className="text-sm text-white">{formatDate(article.publishedAt)}</span>
                      {tier && (
                        <span className={`text-xs px-1.5 py-0.5 rounded border ${tier.color}`} title={tier.title}>
                          {tier.label}
                        </span>
                      )}
                      {article.region && (
                        <span className="text-xs bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded">{article.region}</span>
                      )}
                      {article.reach && (
                        <span className="text-xs text-white">{formatReach(article.reach)} visitors/mo</span>
                      )}
                    </div>
                  </div>

                  {/* Sentiment Buttons */}
                  <div className="flex items-center gap-1 flex-shrink-0 mt-1">
                    {Object.entries(sentimentConfig).map(([key, config]) => {
                      const Icon = config.icon;
                      const isActive = currentSentiment === key;
                      return (
                        <button
                          key={key}
                          onClick={(e) => handleSentiment(e, article.id, isActive ? null : key)}
                          disabled={actioningId === article.id}
                          className={`p-1.5 rounded-md border transition-all ${
                            isActive
                              ? config.color
                              : 'text-white/20 border-transparent hover:text-white hover:border-white'
                          }`}
                          title={`${isActive ? 'Remove' : 'Mark as'} ${config.label}`}
                        >
                          <Icon size={13} />
                        </button>
                      );
                    })}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0 mt-1">
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/20 hover:text-sc-pink transition-colors p-1"
                    >
                      <ExternalLink size={14} />
                    </a>
                    <button
                      onClick={(e) => handleDismiss(e, article.id)}
                      disabled={actioningId === article.id}
                      className="text-white/20 hover:text-orange-400 transition-colors p-1"
                      title="Dismiss article (won't return on next scan)"
                    >
                      {actioningId === article.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <EyeOff size={14} />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Dismissed Articles */}
      {dismissedArticles.length > 0 && (
        <div className="mt-6">
          <button
            onClick={() => setShowDismissed(!showDismissed)}
            className="flex items-center gap-2 text-sm text-white hover:text-white transition-colors mb-2"
          >
            {showDismissed ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            Dismissed Articles ({dismissedArticles.length})
          </button>
          {showDismissed && (
            <div className="bg-gray-900/50 rounded-xl border border-white overflow-hidden">
              <div className="divide-y divide-gray-800/50">
                {dismissedArticles.map((article) => (
                  <div
                    key={article.id}
                    className="flex items-start gap-4 px-5 py-3 opacity-50 hover:opacity-100 transition-opacity"
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm text-white line-clamp-1">
                        {article.title}
                      </h4>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-white">
                        {article.source && <span>{article.source}</span>}
                        <span>{formatDate(article.publishedAt)}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleRestore(e, article.id)}
                      disabled={actioningId === article.id}
                      className="text-white hover:text-green-400 transition-colors p-1 flex-shrink-0"
                      title="Restore article"
                    >
                      {actioningId === article.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <RotateCcw size={14} />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PrCampaignDetail;
